import type { Paginated } from '@fleetcore/shared'
import type { NotificationChannel } from '@fleetcore/shared/telemetry/alerts'

import { api, apiFetch } from '../../lib/api-client'
import { API_PREFIX } from '../../lib/endpoints'
import type {
  AlertCountsView,
  AlertDetailView,
  AlertRuleView,
  AlertView,
  CreateAlertRuleInput,
  NotificationPreferenceView,
  RulePreviewView,
  TestNotificationResultView,
  UpdateAlertRuleInput,
  UpdateNotificationPreferenceInput,
} from './api-types'

/**
 * The alerts module's paths, mirrored from `apps/api/src/alerts/alerts.controller.ts`
 * (`@Controller('alerts')`) and `apps/api/src/notifications/notifications.controller.ts`
 * (`@Controller('notifications')`).
 *
 * ─── THE RULES LIVE UNDER `/alerts/rules`, NOT UNDER `/alert-rules` ─────────
 *
 * One controller owns both, which is why the rule routes are declared BEFORE `:id` on
 * the server: `/alerts/rules` would otherwise be routed into the parameterised handler
 * and rejected by the uuid pipe for a path segment nobody typed. The order does not
 * matter to a client, but this list follows the controller's order so the two files
 * can be compared line by line.
 *
 * ─── AND THE PREFERENCES LIVE UNDER `/notifications` ────────────────────────
 *
 * They are per USER, not per alert, and they carry a notification centre and a test
 * send of their own. `PUT /notifications/preferences/bulk` saves the whole grid in one
 * transaction — the settings page's only write.
 */
export const alertEndpoints = {
  /** GET ?eventType&severity&status&source&vehicleId&from&to&unresolvedOnly&page&pageSize */
  alerts: `${API_PREFIX}/alerts`,
  /** GET → the five figures the feed header needs, in one query. */
  counts: `${API_PREFIX}/alerts/counts`,
  /** GET → the alert AND its timeline. */
  alert: (id: string) => `${API_PREFIX}/alerts/${id}`,
  /** POST { comment } — the comment is REQUIRED (rule 7). */
  acknowledge: (id: string) => `${API_PREFIX}/alerts/${id}/acknowledge`,
  /** POST { comment? } — optional here; the acknowledgement already carries the account. */
  resolve: (id: string) => `${API_PREFIX}/alerts/${id}/resolve`,
  /** POST { comment } */
  reopen: (id: string) => `${API_PREFIX}/alerts/${id}/reopen`,
  /** POST { comment, escalatedTo? } */
  escalate: (id: string) => `${API_PREFIX}/alerts/${id}/escalate`,
  /** POST { comment } — a timeline entry with no status change. Singular, per the route. */
  comment: (id: string) => `${API_PREFIX}/alerts/${id}/comment`,

  /** GET ?eventType&enabled&page&pageSize / POST */
  rules: `${API_PREFIX}/alerts/rules`,
  /** GET / PATCH / DELETE */
  rule: (id: string) => `${API_PREFIX}/alerts/rules/${id}`,
  /** POST { ...rule, days } → the dry run. Writes nothing. */
  rulePreview: `${API_PREFIX}/alerts/rules/preview`,

  /** GET ?eventType&channel → only the rows this user has actually set. */
  preferences: `${API_PREFIX}/notifications/preferences`,
  /** PUT { preferences: [...] } → the whole grid, one round trip, one transaction. */
  preferencesBulk: `${API_PREFIX}/notifications/preferences/bulk`,
  /**
   * POST { channel, phone? } → what the adapter ACTUALLY did.
   *
   * Rule 8's proof rather than its claim: this is how an operator finds out that SMS
   * is a stub before they rely on it for crash alerts, instead of on the night it
   * mattered. The response's `sent` is never upgraded on the way out.
   */
  test: `${API_PREFIX}/notifications/test`,
} as const

function search(params: Record<string, string | number | boolean | undefined>): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    query.set(key, String(value))
  }
  const encoded = query.toString()
  return encoded.length > 0 ? `?${encoded}` : ''
}

export interface ListAlertsParams {
  eventType?: string
  severity?: string
  status?: string
  source?: string
  vehicleId?: string
  driverId?: string
  ruleId?: string
  /** ISO instants. Compared against `occurred_at`, never `detected_at`. */
  from?: string
  to?: string
  unresolvedOnly?: boolean
  page?: number
  pageSize?: number
}

export function fetchAlerts(params: ListAlertsParams = {}): Promise<Paginated<AlertView>> {
  return api.get<Paginated<AlertView>>(alertEndpoints.alerts + search({ ...params }))
}

export function fetchAlertCounts(): Promise<AlertCountsView> {
  return api.get<AlertCountsView>(alertEndpoints.counts)
}

export function fetchAlert(id: string): Promise<AlertDetailView> {
  return api.get<AlertDetailView>(alertEndpoints.alert(id))
}

/**
 * The workflow verbs (rule 7). Each returns the alert with its timeline, so the
 * drawer redraws from the server's own account of what just happened rather than
 * from an optimistic guess — the one screen in this app where a wrong optimistic
 * update would be a false record of who did what.
 */
export function acknowledgeAlert(id: string, comment: string): Promise<AlertDetailView> {
  return api.post<AlertDetailView>(alertEndpoints.acknowledge(id), { comment })
}

export function resolveAlert(id: string, comment?: string): Promise<AlertDetailView> {
  return api.post<AlertDetailView>(
    alertEndpoints.resolve(id),
    comment !== undefined && comment.trim() !== '' ? { comment } : {},
  )
}

export function reopenAlert(id: string, comment: string): Promise<AlertDetailView> {
  return api.post<AlertDetailView>(alertEndpoints.reopen(id), { comment })
}

export function escalateAlert(
  id: string,
  comment: string,
  escalatedTo?: string,
): Promise<AlertDetailView> {
  return api.post<AlertDetailView>(alertEndpoints.escalate(id), {
    comment,
    escalatedTo: escalatedTo === undefined || escalatedTo.trim() === '' ? null : escalatedTo,
  })
}

export function commentOnAlert(id: string, comment: string): Promise<AlertDetailView> {
  return api.post<AlertDetailView>(alertEndpoints.comment(id), { comment })
}

export interface ListRulesParams {
  eventType?: string
  enabled?: boolean
  page?: number
  pageSize?: number
}

export function fetchAlertRules(params: ListRulesParams = {}): Promise<Paginated<AlertRuleView>> {
  return api.get<Paginated<AlertRuleView>>(alertEndpoints.rules + search({ ...params }))
}

export function fetchAlertRule(id: string): Promise<AlertRuleView> {
  return api.get<AlertRuleView>(alertEndpoints.rule(id))
}

export function createAlertRule(input: CreateAlertRuleInput): Promise<AlertRuleView> {
  return api.post<AlertRuleView>(alertEndpoints.rules, input)
}

export function updateAlertRule(id: string, input: UpdateAlertRuleInput): Promise<AlertRuleView> {
  return api.patch<AlertRuleView>(alertEndpoints.rule(id), input)
}

export function deleteAlertRule(id: string): Promise<void> {
  return api.delete<void>(alertEndpoints.rule(id))
}

/**
 * The dry run — a POST that writes nothing.
 *
 * It takes the DRAFT, not a saved rule id, on purpose: the question the builder has
 * to answer is "what would the rule I am typing have done", and answering it only for
 * an already-saved rule means every operator finds out after enabling it.
 */
export function previewAlertRule(
  input: CreateAlertRuleInput,
  days: number,
): Promise<RulePreviewView> {
  return api.post<RulePreviewView>(alertEndpoints.rulePreview, { ...input, days })
}

/**
 * Only the rows this user has actually set. A pair with no row is ON at `info` — the
 * unconfigured default is "tell me", and the settings grid fills the gaps with that
 * rather than rendering an unset row as OFF.
 */
export function fetchNotificationPreferences(): Promise<readonly NotificationPreferenceView[]> {
  return api.get<readonly NotificationPreferenceView[]>(alertEndpoints.preferences)
}

/**
 * The whole grid in one round trip.
 *
 * `PUT` rather than `PATCH` because the API's natural key is
 * (user, eventType, channel) and it upserts with ON CONFLICT — two tabs of the
 * settings page saving at once cannot silently lose one of them. `lib/api-client.ts`
 * exposes no `put`, so this goes through the exported `apiFetch`, which is the same
 * function `api.get` and friends are built on: it keeps the bearer token, the
 * single-flight refresh and the `ApiError` mapping. Calling `fetch` directly here
 * would drop all three.
 */
export function saveNotificationPreferences(
  preferences: readonly UpdateNotificationPreferenceInput[],
): Promise<readonly NotificationPreferenceView[]> {
  return apiFetch<readonly NotificationPreferenceView[]>(alertEndpoints.preferencesBulk, {
    method: 'PUT',
    json: { preferences },
  })
}

/**
 * RULE 8's proof. What the adapter ACTUALLY did, never upgraded.
 *
 * `sent: false` with `outcome: 'not_configured'` is the honest answer for SMS in this
 * phase, and letting an operator produce it themselves is worth more than any label:
 * they find out on a Tuesday afternoon rather than on the night of the crash.
 */
export function sendTestNotification(
  channel: NotificationChannel,
  phone?: string,
): Promise<TestNotificationResultView> {
  return api.post<TestNotificationResultView>(alertEndpoints.test, {
    channel,
    ...(phone === undefined || phone.trim() === '' ? {} : { phone: phone.trim() }),
  })
}
